import nodeCrypto from 'crypto';

import type { NextApiRequest, NextApiResponse } from 'next';

export type SessionData = {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number; // epoch ms
  [key: string]: any;
};

// Dùng biến global để tránh mất session giữa các lần reload trong dev
const g: any = globalThis as any;
if (!g.__APP_SESSION_STORE) {
  g.__APP_SESSION_STORE = new Map<string, SessionData>();
}
const sessionStore: Map<string, SessionData> = g.__APP_SESSION_STORE as Map<string, SessionData>;

function generateSessionId(): string {
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + cryptoRandom();
}

function cryptoRandom(): string {
  const webCrypto: any = (globalThis as any)?.crypto;
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    const buf = new Uint32Array(4);
    webCrypto.getRandomValues(buf);
    return Array.from(buf)
      .map(n => n.toString(36))
      .join('');
  }
  // Node.js fallback
  try {
    return nodeCrypto.randomBytes(16).toString('base64');
  } catch {
    return Math.random().toString(36).slice(2);
  }
}

function parseCookies(req: NextApiRequest): Record<string, string> {
  if (!req) return {};
  const header = req?.headers?.cookie || '';
  return header.split(';').reduce<Record<string, string>>((acc, part) => {
    const [k, ...rest] = part.trim().split('=');
    if (!k) return acc;
    acc[decodeURIComponent(k)] = decodeURIComponent(rest.join('='));
    return acc;
  }, {});
}

export function serializeCookie(
  name: string,
  value: string,
  options: {
    httpOnly?: boolean;
    secure?: boolean;
    path?: string;
    sameSite?: 'Lax' | 'Strict' | 'None';
    maxAge?: number; // seconds
  } = {}
): string {
  const segments = [`${encodeURIComponent(name)}=${encodeURIComponent(value)}`];
  if (options.maxAge && Number.isFinite(options.maxAge))
    segments.push(`Max-Age=${Math.floor(options.maxAge)}`);
  segments.push(`Path=${options.path || '/'}`);
  segments.push(`SameSite=${options.sameSite || 'Lax'}`);
  if (options.httpOnly !== false) segments.push('HttpOnly');
  if (options.secure !== false) segments.push('Secure');
  return segments.join('; ');
}

const SESSION_COOKIE_NAME = 'sid';
const DEFAULT_SESSION_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days

export function createSession(
  req: NextApiRequest,
  res: NextApiResponse,
  data: SessionData,
  ttlSeconds: number = DEFAULT_SESSION_TTL_SECONDS
): { sessionId: string } {
  const sid = req.cookies[SESSION_COOKIE_NAME];
  // Check if the sid exists and is a valid session in sessionStore; if not, generate a new sessionId
  const sessionId = sid && sessionStore.has(sid) ? sid : generateSessionId();
  const record: SessionData = { ...data };

  record.expiresAt = record.exp ?? Date.now() + ttlSeconds * 1000;
  sessionStore.set(sessionId, record);

  const cookie = serializeCookie(SESSION_COOKIE_NAME, sessionId, {
    httpOnly: true,
    secure: process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_HTTPS === 'true',
    sameSite: 'Lax',
    path: '/',
    maxAge: ttlSeconds,
  });
  const existing = res.getHeader('Set-Cookie');
  if (existing) {
    const arr = Array.isArray(existing) ? existing : [String(existing)];
    res.setHeader('Set-Cookie', [...arr, cookie]);
  } else {
    res.setHeader('Set-Cookie', cookie);
  }

  return { sessionId };
}

export function getSession(req: NextApiRequest): { sessionId: string; data: SessionData } | null {
  if (!req) return null;

  const cookies = parseCookies(req);
  const sessionId = cookies[SESSION_COOKIE_NAME];

  if (!sessionId) return { sessionId: '', data: {} };
  const record = sessionStore.get(sessionId);

  if (!record) return { sessionId: '', data: {} };
  if (record.expiresAt && Date.now() > record.expiresAt) {
    sessionStore.delete(sessionId);
    return { sessionId: '', data: {} };
  }
  return { sessionId, data: record };
}

export function updateSession(
  req: NextApiRequest,
  _res: NextApiResponse,
  partial: Partial<SessionData>
): boolean {
  const current = getSession(req);
  if (!current) return false;
  const next: SessionData = { ...current.data, ...partial };
  sessionStore.set(current.sessionId, next);

  return true;
}

export function destroySession(req: NextApiRequest, res: NextApiResponse): void {
  if (!req || !res) return;
  const cookies = parseCookies(req);
  const sessionId = cookies[SESSION_COOKIE_NAME];
  if (sessionId) sessionStore.delete(sessionId);

  setClientAuthFlag(res, false);
}

// Helper: đặt cookie client-side (không HttpOnly) để FE đọc trạng thái auth nhanh
export function setClientAuthFlag(res: NextApiResponse, isAuthed: boolean, ttlSeconds?: number) {
  if (!res) return;
  const cookie = serializeCookie('auth', isAuthed ? '1' : '0', {
    httpOnly: false,
    secure: process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_HTTPS === 'true',
    sameSite: 'Lax',
    path: '/',
    maxAge: ttlSeconds && ttlSeconds > 0 ? ttlSeconds : undefined,
  });
  const existing = res.getHeader('Set-Cookie');
  if (existing) {
    const arr = Array.isArray(existing) ? existing : [String(existing)];
    res.setHeader('Set-Cookie', [...arr, cookie]);
  } else {
    res.setHeader('Set-Cookie', cookie);
  }
}

export function requireAuthFromSession(req: NextApiRequest): SessionData | null {
  const session = getSession(req);
  if (!session) return null;
  return session.data;
}
